import { useCallback, useState } from "react";
import { Task, TaskStatus } from "@common/types/task";
import { isTerminatingStatus } from "@components/tasks/TaskStatusSelect/taskStatus.constants";
import {
    useUserMetaQuery,
    useUpdateUserMetaMutation,
    userMetaQueryKey,
} from "@api/user.api";
import { useQueryClient } from "@tanstack/react-query";
import { formatISO } from "date-fns";

interface UseTerminatingStatusGuardReturn {
    handleStatusChange: (newStatus: TaskStatus) => Promise<void>;
    pendingStatus: TaskStatus | null;
    activeBlockerCount: number;
    confirmChange: () => Promise<void>;
    cancelChange: () => void;
    showDontAskAgain: boolean;
    shouldSkipConfirmation: boolean;
    setShouldSkipConfirmation: (val: boolean) => void;
}

/**
 * Whether moving `task` to `newStatus` needs the user's confirmation first:
 * it becomes Done or Canceled while still blocked by an open task, and the
 * user hasn't opted out ("Don't ask me again").
 */
export const needsTerminatingStatusConfirmation = ({
    task,
    newStatus,
    isAutoMarkSet,
}: {
    task: Pick<Task, "status" | "numberOfActiveBlockedByTasks">;
    newStatus: TaskStatus;
    isAutoMarkSet: boolean;
}) => {
    const isMovingFromTerminatingToTerminating =
        isTerminatingStatus(task.status) && isTerminatingStatus(newStatus);

    return (
        isTerminatingStatus(newStatus) &&
        (task.numberOfActiveBlockedByTasks ?? 0) > 0 &&
        !isMovingFromTerminatingToTerminating &&
        !isAutoMarkSet
    );
};

/**
 * The user's "Don't ask me again" choice for finishing blocked tasks:
 * whether it is set, and `rememberAutoMark` to set it.
 */
export function useAutoMarkBlockedTaskAsDone() {
    const { data: userMeta } = useUserMetaQuery();
    const { mutate: updateUserMeta } = useUpdateUserMetaMutation();
    const queryClient = useQueryClient();

    const rememberAutoMark = useCallback(() => {
        const now = formatISO(new Date());
        const previousMeta = queryClient.getQueryData([userMetaQueryKey]);
        // Optimistically update cache so the modal won't show again
        // immediately; the actual PATCH fires in the background.
        queryClient.setQueryData([userMetaQueryKey], {
            autoMarkBlockedTaskAsDoneAt: now,
        });
        updateUserMeta(
            { autoMarkBlockedTaskAsDoneAt: now },
            {
                onError: () => {
                    queryClient.setQueryData([userMetaQueryKey], previousMeta);
                },
            },
        );
    }, [queryClient, updateUserMeta]);

    return {
        isAutoMarkSet: Boolean(userMeta?.autoMarkBlockedTaskAsDoneAt),
        rememberAutoMark,
    };
}

/** A status change waiting on the user's confirmation. */
export interface PendingStatusChange<TTask> {
    task: TTask;
    status: TaskStatus;
}

/**
 * Status changes for any task, asking first when one would finish a task
 * that still has open blockers. `apply` runs a change at once, or from
 * `confirmChange` once the user agrees; one change waits at a time.
 */
export function useTerminatingStatusConfirmation<
    TTask extends Pick<Task, "status" | "numberOfActiveBlockedByTasks">,
>(apply: (change: PendingStatusChange<TTask>) => Promise<unknown>) {
    const [pending, setPending] = useState<PendingStatusChange<TTask> | null>(
        null,
    );
    const [shouldSkipConfirmation, setShouldSkipConfirmation] = useState(false);

    const { isAutoMarkSet, rememberAutoMark } = useAutoMarkBlockedTaskAsDone();

    const changeStatus = useCallback(
        async (change: PendingStatusChange<TTask>) => {
            if (
                needsTerminatingStatusConfirmation({
                    task: change.task,
                    newStatus: change.status,
                    isAutoMarkSet,
                })
            ) {
                setPending(change);
                return;
            }
            await apply(change);
        },
        [apply, isAutoMarkSet],
    );

    const confirmChange = useCallback(async () => {
        if (pending) {
            if (shouldSkipConfirmation) {
                rememberAutoMark();
            }
            await apply(pending);
            setPending(null);
            setShouldSkipConfirmation(false);
        }
    }, [pending, shouldSkipConfirmation, apply, rememberAutoMark]);

    const cancelChange = useCallback(() => {
        setPending(null);
        setShouldSkipConfirmation(false);
    }, []);

    return {
        changeStatus,
        pending,
        confirmChange,
        cancelChange,
        showDontAskAgain: !isAutoMarkSet,
        shouldSkipConfirmation,
        setShouldSkipConfirmation,
    };
}

/**
 * Wraps a task status update with a confirmation guard:
 * if the new status is terminating (Done / Cancelled) and the task
 * is still blocked by one or more non-terminated tasks, the caller
 * must show a confirmation UI before the update fires.
 *
 * If the user has previously opted in via autoMarkBlockedTaskAsDoneAt,
 * the confirmation is skipped entirely.
 *
 * Usage:
 *   const { handleStatusChange, pendingStatus, activeBlockerCount,
 *           confirmChange, cancelChange,
 *           showDontAskAgain, shouldSkipConfirmation, setShouldSkipConfirmation } =
 *       useTerminatingStatusGuard(task, updateFn);
 *
 *   // render a modal when pendingStatus !== null
 */
export function useTerminatingStatusGuard(
    task: Task,
    updateStatus: (status: TaskStatus) => Promise<unknown>,
): UseTerminatingStatusGuardReturn {
    const applyStatus = useCallback(
        ({ status }: PendingStatusChange<Task>) => updateStatus(status),
        [updateStatus],
    );
    const { changeStatus, pending, ...confirmation } =
        useTerminatingStatusConfirmation(applyStatus);

    const handleStatusChange = useCallback(
        (newStatus: TaskStatus) => changeStatus({ task, status: newStatus }),
        [changeStatus, task],
    );

    return {
        handleStatusChange,
        pendingStatus: pending?.status ?? null,
        activeBlockerCount: task.numberOfActiveBlockedByTasks ?? 0,
        ...confirmation,
    };
}
